import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Modal, Spinner, Tab, Tabs } from 'react-bootstrap';
import { usePagedArray, Pager } from 'rentalui-finance';
import ResponsiveTable from './ResponsiveTable';
import {
  getInvoices, fetchInvoicePdfBlob, saveInvoicePdfBlob, updateInvoiceStatus, recordInvoicePayment
} from '../api';

// manual_vs_automatic_invoicing.md: manual invoice creation is feature-disabled for these
// verticals; only AUTOMATIC invoices (generated on booking completion) are shown.
const MANUAL_INVOICING_ENABLED = false; // set true to reveal New Invoice + Invoice Format tabs

const rupees = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN') : '—';

function paymentBadge(inv) {
  if (inv.revenueRecognisedAt) return <Badge bg="success">Verified</Badge>;
  if (inv.financeReview?.decision === 'DISPUTED') return <Badge bg="danger">Disputed</Badge>;
  if (inv.paymentStatus === 'PAID' || inv.paymentStatus === 'PARTIALLY_PAID') {
    return <Badge bg="warning" text="dark">Paid · awaiting verification</Badge>;
  }
  return <Badge bg="light" text="dark" className="border">Unpaid</Badge>;
}

function PdfPreviewModal({ invoiceId, invoiceNumber, show, onHide }) {
  const [blob, setBlob] = useState(null);
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!show || !invoiceId) return undefined;
    let active = true;
    setLoading(true); setError(''); setBlob(null); setUrl('');
    fetchInvoicePdfBlob(invoiceId)
      .then((b) => {
        if (!active) return;
        setBlob(b);
        setUrl(window.URL.createObjectURL(b));
      })
      .catch(() => { if (active) setError('Unable to load the PDF preview.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [show, invoiceId]);

  useEffect(() => () => { if (url) window.URL.revokeObjectURL(url); }, [url]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title as="h6" className="mb-0">Invoice preview — {invoiceNumber}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0" style={{ minHeight: '60vh' }}>
        {loading && <div className="p-5 text-center"><Spinner animation="border" size="sm" /></div>}
        {error && <Alert variant="danger" className="m-3">{error}</Alert>}
        {url && !loading && (
          <iframe title="Invoice PDF preview" src={url} style={{ width: '100%', height: '75vh', border: 0, display: 'block' }} />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Close</Button>
        <Button variant="success" disabled={!blob} onClick={() => saveInvoicePdfBlob(blob, invoiceNumber)}>Download PDF</Button>
      </Modal.Footer>
    </Modal>
  );
}

function HistoryTab({ reload }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewInv, setPreviewInv] = useState(null);
  const [toggling, setToggling] = useState(null);

  const paged = usePagedArray(invoices, 20);

  const load = useCallback(() => {
    setLoading(true); setError('');
    getInvoices()
      .then((data) => setInvoices(data.data || data || []))
      .catch(() => setError('Unable to load invoices.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load, reload]);

  const toggleStatus = async (inv) => {
    setToggling(inv._id);
    try {
      await updateInvoiceStatus(inv._id, inv.status === 'active' ? 'disabled' : 'active');
      load();
    } catch {
      setError('Unable to update invoice status.');
    } finally {
      setToggling(null);
    }
  };

  if (loading) return <div className="py-4 text-center"><Spinner animation="border" size="sm" /></div>;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (invoices.length === 0) {
    return (
      <Alert variant="light">
        No invoices yet. Invoices are automatically generated when a booking is completed.
      </Alert>
    );
  }

  return (
    <>
      <ResponsiveTable
        rows={paged.rows}
        rowKey={(inv) => inv._id}
        empty="No invoices."
        columns={[
          {
            key: 'number', header: 'Invoice #', card: 'title',
            cell: (inv) => (
              <span className={inv.status === 'disabled' ? 'text-decoration-line-through text-muted' : ''}>
                {inv.invoiceNumber}
              </span>
            )
          },
          { key: 'date', header: 'Date', cell: (inv) => fmtDate(inv.invoiceDate) },
          {
            key: 'customer', header: 'Customer',
            cell: (inv) => inv.customer?.name || '—'
          },
          {
            key: 'amount', header: 'Amount', className: 'text-end',
            cell: (inv) => rupees(inv.totals?.totalAmount)
          },
          { key: 'status', header: 'Status', cell: (inv) => paymentBadge(inv) },
          {
            key: 'actions', header: '', card: 'actions',
            cell: (inv) => (
              <div className="d-flex gap-1 flex-wrap">
                <Button size="sm" variant="outline-secondary" onClick={() => setPreviewInv(inv)}>Preview</Button>
                <Button
                  size="sm"
                  variant={inv.status === 'active' ? 'outline-danger' : 'outline-success'}
                  disabled={toggling === inv._id}
                  onClick={() => toggleStatus(inv)}
                >
                  {inv.status === 'active' ? 'Disable' : 'Enable'}
                </Button>
              </div>
            )
          }
        ]}
      />
      <Pager {...paged} />
      {previewInv && (
        <PdfPreviewModal
          invoiceId={previewInv._id}
          invoiceNumber={previewInv.invoiceNumber}
          show
          onHide={() => setPreviewInv(null)}
        />
      )}
    </>
  );
}

export default function InvoicesPanel() {
  const [reload, setReload] = useState(0);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h6 className="mb-0">Invoices</h6>
        <div className="small text-muted">
          Invoices are auto-generated when a booking is completed.
        </div>
      </div>

      {MANUAL_INVOICING_ENABLED ? (
        <Tabs defaultActiveKey="history" className="mb-3">
          <Tab eventKey="history" title="History">
            <HistoryTab reload={reload} />
          </Tab>
          {/* New Invoice and Invoice Format tabs are available as code but hidden until
              MANUAL_INVOICING_ENABLED is set to true. */}
        </Tabs>
      ) : (
        <HistoryTab reload={reload} />
      )}
    </div>
  );
}
